import { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { FileText, Camera, Stethoscope, Syringe, User, ImageIcon, Loader2, MoreHorizontal, X, Plus, Trash2, Pencil, ArrowLeftRight, RotateCw, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import {
    AccessDeniedFallback,
    isAccessDeniedError,
    getMissingPermissions,
} from '~/components/access-control';
import { AppointmentChatPanel } from '~/components/appointments/AppointmentChat';
import { ButtonOpenTreatmentPlanner } from '~/components/customer/ButtonOpenTreatmentPlanner';
import {
    AddNoteDialog,
    AddImageDialog,
    AddTreatmentDialog,
} from '~/components/medical-record';
import { NO_TREATMENT_REASON_LABELS } from '~/components/medical-record/noTreatmentReasons';
import type { MedicalRecordNoTreatmentReason } from '@server/prisma-generated/enums';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Badge } from '~/components/ui/badge';
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { Input } from '~/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Label } from '~/components/ui/label';
import { AutoComplete } from '~/components/ui/autocomplete';
import { cn } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';

type MedicalRecordSectionProps = {
    customerId: number;
    customerNote?: string | null;
};

const MedicalRecordSection = ({ customerId, customerNote }: MedicalRecordSectionProps) => {
    const { data, isLoading, error } = trpc.medicalRecord.getByCustomerId.useQuery(
        { customerId },
        { retry: false } // Don't retry on FORBIDDEN
    );

    // Handle access denied
    if (isAccessDeniedError(error)) {
        return (
            <AccessDeniedFallback
                variant="blurred-card"
                missingPermissions={getMissingPermissions(error)}
                showContactAdmin={true}
            />
        );
    }

    // Handle loading
    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                </CardContent>
            </Card>
        );
    }

    // Handle other errors
    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-500">
                    Fehler beim Laden der Patientenakte
                </CardContent>
            </Card>
        );
    }

    // Render medical record content
    return <MedicalRecordContent record={data} customerId={customerId} customerNote={customerNote} />;
};

// Subcomponent for the actual content
type MedicalRecordContentProps = {
    record: {
        id: string;
        notes: Array<{
            id: string;
            content: string;
            createdAt: string;
            practitionerId: number;
            practitioner: {
                firstName: string;
                lastName: string;
            };
        }>;
        treatments: Array<{
            id: string;
            treatmentDate: string;
            createdAt: string;
            documentationStep: string;
            diagnosis: string;
            notes: string;
            noTreatmentReason: MedicalRecordNoTreatmentReason | null;
            administeredByPractitionerId: number;
            administeredByPractitioner: {
                firstName: string;
                lastName: string;
            };
            appointment: {
                id: string;
                steps: { dateFrom: string }[];
            } | null;
            medications: Array<{
                id: string;
                medicationName: string | null;
                medicationQuantity: number | string;
                medicationUnit: string | null;
                medicationRegion: string | null;
                notes: string;
                medicament: { id: string; name: string; unit: { id: string; name: string } | null } | null;
                region: { id: string; name: string } | null;
            }>;
            cartItemAttributions: Array<{
                id: string;
                cartItem?: {
                    treatmentVariant: { name: string };
                } | null;
                treatmentVariant?: { id: string; name: string; treatment?: { name: string } | null } | null;
                physicalProduct?: { id: string; name: string } | null;
            }>;
        }>;
        images: Array<{
            id: string;
            bodyRegion: string;
            bodyRegionId: string | null;
            description: string;
            imageType: string;
            createdAt: string;
            appointmentId: string | null;
            uploadedByPractitionerId: number | null;
            uploadedByPractitioner: {
                firstName: string;
                lastName: string;
            } | null;
        }>;
    } | null | undefined;
    customerId: number;
    customerNote?: string | null;
};

const ALLOWED_APPOINTMENT_STATUSES = ['SCHEDULED', 'COMPLETED', 'REQUESTED'];

const MedicalRecordContent = ({ record, customerId, customerNote }: MedicalRecordContentProps) => {
    const currentPractitionerId = useUserStore((state) => state.user?.practitionerId ?? null);
    const practitionerCategoryId = useUserStore((state) => state.user?.practitionerCategoryId ?? null);
    const canCreateDocumentation = practitionerCategoryId === 1 || practitionerCategoryId === 2;
    const canUploadPhotos = currentPractitionerId !== null;
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [addTreatmentOpen, setAddTreatmentOpen] = useState(false);
    const [addImageOpen, setAddImageOpen] = useState(false);
    const [editTreatmentId, setEditTreatmentId] = useState<string | null>(null);
    const [editAttributionTreatmentId, setEditAttributionTreatmentId] = useState<string | null>(null);
    const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
    const [deleteTreatmentId, setDeleteTreatmentId] = useState<string | null>(null);
    const [docStartAppointmentId, setDocStartAppointmentId] = useState<string | null>(null);
    const [addImageForAppointmentId, setAddImageForAppointmentId] = useState<string | null>(null);
    const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
    const [compareMode, setCompareMode] = useState(false);
    const [compareImageId, setCompareImageId] = useState<string | null>(null);
    const [filterAppointmentId, setFilterAppointmentId] = useState<string | null>(null);
    const [expandedTreatmentIds, setExpandedTreatmentIds] = useState<Set<string>>(() => new Set());

    const utils = trpc.useUtils();

    const expandableTreatmentIds = useMemo(
        () => (record?.treatments ?? [])
            .filter((t) => t.documentationStep !== 'ATTRIBUTION_STEP' && !!t.notes)
            .map((t) => t.id),
        [record?.treatments]
    );
    const allExpanded = expandableTreatmentIds.length > 0 && expandableTreatmentIds.every((id) => expandedTreatmentIds.has(id));

    const toggleTreatmentExpanded = (treatmentId: string) => {
        setExpandedTreatmentIds((prev) => {
            const next = new Set(prev);
            if (next.has(treatmentId)) {
                next.delete(treatmentId);
            } else {
                next.add(treatmentId);
            }
            return next;
        });
    };

    const toggleAllExpanded = () => {
        setExpandedTreatmentIds(allExpanded ? new Set() : new Set(expandableTreatmentIds));
    };

    const { data: appointments } = trpc.appointments.getByCustomerId.useQuery({ customerId });

    const todaysAppointment = useMemo(() => {
        if (!appointments) {
            return null;
        }
        const now = new Date();
        const candidates = appointments
            .filter((a) => ALLOWED_APPOINTMENT_STATUSES.includes(a.status))
            .map((a) => ({ appointment: a, dateFrom: a.steps?.[0]?.dateFrom ? new Date(a.steps[0].dateFrom) : null }))
            .filter((a): a is { appointment: typeof appointments[number]; dateFrom: Date } =>
                a.dateFrom != null &&
                a.dateFrom.getFullYear() === now.getFullYear() &&
                a.dateFrom.getMonth() === now.getMonth() &&
                a.dateFrom.getDate() === now.getDate()
            )
            .sort((a, b) => b.dateFrom.getTime() - a.dateFrom.getTime());
        return candidates[0] ?? null;
    }, [appointments]);

    const todaysAppointmentTreatment = trpc.medicalRecord.getLatestTreatmentByAppointment.useQuery(
        { appointmentId: todaysAppointment?.appointment.id ?? '' },
        { enabled: !!todaysAppointment }
    );

    const imageAppointmentOptions = useMemo(() => {
        if (!record?.images || !appointments) return [];
        const appointmentIdsWithImages = new Set(
            record.images.map((img) => img.appointmentId).filter(Boolean)
        );
        return appointments
            .filter((a) => appointmentIdsWithImages.has(a.id))
            .map((a) => {
                const firstStep = a.steps?.[0];
                const dateStr = firstStep?.dateFrom
                    ? format(new Date(firstStep.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })
                    : 'Kein Datum';
                const treatments = a.originalTreatmentVariants
                    ?.map((tv: any) => tv.treatmentVariant?.name)
                    .filter(Boolean)
                    .join(', ') || 'Keine Behandlung';
                return { id: a.id, label: `${dateStr} - ${treatments}` };
            });
    }, [record?.images, appointments]);

    const filteredImages = useMemo(() => {
        if (!record?.images) return [];
        if (!filterAppointmentId) return record.images;
        return record.images.filter((img) => img.appointmentId === filterAppointmentId);
    }, [record?.images, filterAppointmentId]);

    const deleteNoteMutation = trpc.medicalRecord.deleteNote.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            toast.success('Notiz gelöscht');
            setDeleteNoteId(null);
        },
        onError: (error) => toast.error('Fehler beim Löschen', { description: error.message }),
    });

    const deleteTreatmentMutation = trpc.medicalRecord.deleteTreatment.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            utils.medicalRecord.getLatestTreatmentByAppointment.invalidate();
            toast.success('Behandlung gelöscht');
            setDeleteTreatmentId(null);
        },
        onError: (error) => toast.error('Fehler beim Löschen', { description: error.message }),
    });

    if (!record) {
        return (
            <>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Patientenakte
                        </CardTitle>
                        <CardDescription>
                            Noch keine Patientenakte vorhanden
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-gray-500 text-sm mb-4">
                            Die Patientenakte wird automatisch erstellt, sobald der erste Eintrag hinzugefügt wird.
                        </p>
                        {(canCreateDocumentation || canUploadPhotos) && (
                            <div className="flex gap-2">
                                {canCreateDocumentation && (
                                    <Button variant="outline" size="sm" onClick={() => setAddNoteOpen(true)}>
                                        <User className="h-4 w-4 mr-2" />
                                        Notiz
                                    </Button>
                                )}
                                {canUploadPhotos && (
                                    <Button variant="outline" size="sm" onClick={() => setAddImageOpen(true)}>
                                        <Camera className="h-4 w-4 mr-2" />
                                        Bild
                                    </Button>
                                )}
                                {canCreateDocumentation && (
                                    <Button variant="default" size="sm" onClick={() => setAddTreatmentOpen(true)}>
                                        <Stethoscope className="h-4 w-4 mr-2" />
                                        Behandlung
                                    </Button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <AddNoteDialog customerId={customerId} open={addNoteOpen} onOpenChange={setAddNoteOpen} />
                <AddImageDialog customerId={customerId} open={addImageOpen} onOpenChange={setAddImageOpen} />
                <AddTreatmentDialog customerId={customerId} forceNew open={addTreatmentOpen} onOpenChange={setAddTreatmentOpen} />
            </>
        );
    }

    const getTreatmentDisplayDate = (treatment: MedicalRecordContentProps['record'] extends infer R ? R extends { treatments: (infer T)[] } ? T : never : never) => {
        const appointmentDate = treatment.appointment?.steps?.[0]?.dateFrom;
        return appointmentDate ? new Date(appointmentDate) : new Date(treatment.treatmentDate);
    };

    return (
        <div className="space-y-3">
            {/* Behandlungsplaner + Internal Customer Note */}
            <div className="flex items-start gap-3">
                <ButtonOpenTreatmentPlanner customerId={customerId} />
                {customerNote && (
                    <Card className="border-yellow-400 bg-yellow-50 dark:bg-yellow-950/20 flex-1 min-w-0">
                        <div className="px-4 py-2">
                            <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300">Interne Kundennotiz</p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-0.5 whitespace-pre-line">{customerNote}</p>
                        </div>
                    </Card>
                )}
            </div>

            {/* Notes Section */}
            <Card>
                <div className="px-3 py-2">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            Medizinische Notizen ({record.notes.length})
                        </span>
                        {canCreateDocumentation && (
                            <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => setAddNoteOpen(true)}>
                                <Plus className="h-3 w-3 mr-1" />
                                Neue Notiz
                            </Button>
                        )}
                    </div>
                    {record.notes.length === 0 ? (
                        <p className="text-muted-foreground text-xs">Keine Notizen vorhanden</p>
                    ) : (
                        <div className="space-y-2">
                            {record.notes.map((note) => (
                                <div key={note.id} className="border-l-2 border-gray-200 pl-3 flex items-start justify-between gap-1">
                                    <div className="flex-1">
                                        <p className="text-xs text-muted-foreground">
                                            {format(new Date(note.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })} - {note.practitioner.firstName} {note.practitioner.lastName}
                                        </p>
                                        <p className="text-sm text-red-600">{note.content}</p>
                                    </div>
                                    {note.practitionerId === currentPractitionerId ? (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-6 w-6 p-0 shrink-0">
                                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    className="text-destructive"
                                                    onClick={() => setDeleteNoteId(note.id)}
                                                >
                                                    Löschen
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    ) : (
                                        <Button variant="ghost" className="h-6 w-6 p-0 shrink-0 opacity-30 pointer-events-none">
                                            <MoreHorizontal className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            {/* Today's Appointment Card */}
            {todaysAppointment && (
                <Card className="border-green-500 bg-green-50 dark:bg-green-950/20 gap-0">
                    <div className="px-4 py-3 flex items-start justify-between gap-3">
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center gap-1.5">
                                <Stethoscope className="h-4 w-4" />
                                Heutiger Termin
                            </p>
                            <p className="text-sm mt-1">
                                {format(todaysAppointment.dateFrom, 'dd.MM.yyyy HH:mm', { locale: de })} Uhr
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {todaysAppointment.appointment.originalTreatmentVariants
                                    ?.map((tv) => tv.treatmentVariant?.name)
                                    .filter(Boolean)
                                    .join(', ') || 'Keine Behandlung gebucht'}
                            </p>
                            {(todaysAppointment.appointment as any).messageFromCustomer && (
                                <div className="mt-1.5 rounded px-2 py-1 bg-yellow-100 text-black">
                                    <p className="text-xs font-medium">Nachricht vom Kunden</p>
                                    <p className="text-xs mt-0.5 whitespace-pre-line">{(todaysAppointment.appointment as any).messageFromCustomer}</p>
                                </div>
                            )}
                            {(todaysAppointment.appointment as any).appointmentNotes && (
                                <div className="mt-1 rounded px-2 py-1 bg-blue-100 text-black">
                                    <p className="text-xs font-medium">Termin-Notiz (intern)</p>
                                    <p className="text-xs mt-0.5 whitespace-pre-line">{(todaysAppointment.appointment as any).appointmentNotes}</p>
                                </div>
                            )}
                        </div>
                        {(canUploadPhotos || canCreateDocumentation) && (
                            <div className="flex items-center gap-2 shrink-0">
                                {canUploadPhotos && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="shrink-0"
                                        onClick={() => setAddImageForAppointmentId(todaysAppointment.appointment.id)}
                                    >
                                        <Camera className="h-4 w-4 mr-2" />
                                        Foto
                                    </Button>
                                )}
                                {canCreateDocumentation && (
                                    <>
                                        {todaysAppointmentTreatment.data?.documentationStep === 'ATTRIBUTION_STEP' && todaysAppointmentTreatment.data?.administeredByPractitionerId === currentPractitionerId ? (
                                            <Button
                                                size="sm"
                                                className="bg-yellow-500 hover:bg-yellow-600 text-black shrink-0"
                                                onClick={() => setEditTreatmentId(todaysAppointmentTreatment.data!.id)}
                                            >
                                                <Stethoscope className="h-4 w-4 mr-2" />
                                                Medizinische Doku starten
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                                                onClick={() => setDocStartAppointmentId(todaysAppointment.appointment.id)}
                                            >
                                                <Stethoscope className="h-4 w-4 mr-2" />
                                                Neue Dokumentation starten
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="border-t border-green-200 px-4 py-3">
                        <AppointmentChatPanel appointmentId={todaysAppointment.appointment.id} />
                    </div>
                </Card>
            )}

            {/* Treatments Section - Table Layout */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Stethoscope className="h-4 w-4" />
                                Behandlungen ({record.treatments.length})
                            </CardTitle>
                            {expandableTreatmentIds.length > 0 && (
                                <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={toggleAllExpanded}>
                                    {allExpanded ? (
                                        <ChevronsDownUp className="h-3.5 w-3.5 mr-1" />
                                    ) : (
                                        <ChevronsUpDown className="h-3.5 w-3.5 mr-1" />
                                    )}
                                    {allExpanded ? 'Alle einklappen' : 'Alle ausklappen'}
                                </Button>
                            )}
                        </div>
                        {canCreateDocumentation && (
                            <Button variant="outline" size="sm" onClick={() => setAddTreatmentOpen(true)}>
                                <Stethoscope className="h-4 w-4 mr-2" />
                                Neue Behandlung
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {record.treatments.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-4">
                            <p className="text-gray-500 text-sm">Keine Behandlungen dokumentiert</p>
                            {canCreateDocumentation && (
                                <Button variant="outline" size="sm" onClick={() => setAddTreatmentOpen(true)}>
                                    <Stethoscope className="h-4 w-4 mr-2" />
                                    Neue Behandlung
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="rounded-md border overflow-hidden [&_[data-slot=table-container]]:overflow-hidden">
                            <Table className="w-full table-fixed">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[90px]">Datum</TableHead>
                                        <TableHead className="w-[120px]">Behandler</TableHead>
                                        <TableHead>Notizen</TableHead>
                                        <TableHead className="w-[190px]">Medikation</TableHead>
                                        <TableHead className="w-[40px]" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {record.treatments.map((treatment) => {
                                        const displayDate = getTreatmentDisplayDate(treatment);
                                        const isIncomplete = treatment.documentationStep === 'ATTRIBUTION_STEP';
                                        return (
                                            <TableRow
                                                key={treatment.id}
                                                className={cn(
                                                    "transition-colors align-top",
                                                    treatment.administeredByPractitionerId === currentPractitionerId && "cursor-pointer hover:bg-muted/50",
                                                    isIncomplete
                                                        ? "bg-yellow-50 dark:bg-yellow-950/20"
                                                        : "even:bg-muted/30"
                                                )}
                                                onClick={() => {
                                                    if (treatment.administeredByPractitionerId === currentPractitionerId) {
                                                        setEditTreatmentId(treatment.id);
                                                    }
                                                }}
                                            >
                                                <TableCell className="font-medium whitespace-nowrap align-top">
                                                    {format(displayDate, 'dd.MM.yyyy', { locale: de })}
                                                    <br />
                                                    <span className="text-xs text-muted-foreground">
                                                        {format(displayDate, 'HH:mm', { locale: de })}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="align-top whitespace-normal">
                                                    {treatment.administeredByPractitioner.firstName} {treatment.administeredByPractitioner.lastName}
                                                </TableCell>
                                                <TableCell className="align-top whitespace-normal">
                                                    {(() => {
                                                        const serviceNames = treatment.cartItemAttributions
                                                            .filter(a => a.cartItem || a.treatmentVariant)
                                                            .map(a => a.cartItem?.treatmentVariant?.name ?? a.treatmentVariant?.name ?? '')
                                                            .filter(Boolean);
                                                        const productNames = treatment.cartItemAttributions
                                                            .filter(a => a.physicalProduct)
                                                            .map(a => a.physicalProduct!.name);
                                                        return (
                                                            <>
                                                                <div className="text-xs space-y-0.5 mb-3">
                                                                    <p className="flex items-center gap-1">
                                                                        <span className="font-medium">Behandlung:</span>{' '}
                                                                        {treatment.noTreatmentReason
                                                                            ? <span className="text-red-600">Keine Behandlung durchgeführt – {NO_TREATMENT_REASON_LABELS[treatment.noTreatmentReason]}</span>
                                                                            : serviceNames.length > 0
                                                                                ? serviceNames.join(', ')
                                                                                : new Date(treatment.createdAt) >= new Date('2026-09-27')
                                                                                    ? <span className="text-red-600">Keine Behandlung dokumentiert</span>
                                                                                    : null
                                                                        }
                                                                        {treatment.administeredByPractitionerId === currentPractitionerId && (
                                                                            <Pencil
                                                                                className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-primary inline-block ml-1 shrink-0"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setEditAttributionTreatmentId(treatment.id);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </p>
                                                                    {productNames.length > 0 && (
                                                                        <p>
                                                                            <span className="font-medium">Produkte:</span>{' '}
                                                                            {productNames.join(', ')}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {isIncomplete ? (
                                                                    treatment.administeredByPractitionerId === currentPractitionerId ? (
                                                                        <div className="flex items-center justify-center">
                                                                            <Button
                                                                                size="sm"
                                                                                className="bg-yellow-500 hover:bg-yellow-600 text-black"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setEditTreatmentId(treatment.id);
                                                                                }}
                                                                            >
                                                                                <Stethoscope className="h-4 w-4 mr-2" />
                                                                                Medizinische Doku starten
                                                                            </Button>
                                                                        </div>
                                                                    ) : null
                                                                ) : (
                                                                    <>
                                                                        <div className="text-xs mb-2">
                                                                            <span className="font-medium">Diagnose:</span>{' '}
                                                                            {treatment.diagnosis || '–'}
                                                                            {(treatment as any).mainIcdDisplay && (
                                                                                <span className="ml-2 text-muted-foreground">| Haupt-ICD: {(treatment as any).mainIcdDisplay}</span>
                                                                            )}
                                                                            {(treatment as any).secondaryIcdDisplay && (
                                                                                <span className="ml-2 text-muted-foreground">| Neben-ICD: {(treatment as any).secondaryIcdDisplay}</span>
                                                                            )}
                                                                        </div>
                                                                        {treatment.notes ? (
                                                                            <TreatmentNotes
                                                                                html={treatment.notes}
                                                                                expanded={expandedTreatmentIds.has(treatment.id)}
                                                                                onToggle={() => toggleTreatmentExpanded(treatment.id)}
                                                                            />
                                                                        ) : (
                                                                            <span className="text-muted-foreground">—</span>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {(() => {
                                                                    if (!treatment.appointment?.id || !record?.images) return null;
                                                                    const appointmentImageIds = record.images
                                                                        .filter((img) => img.appointmentId === treatment.appointment!.id)
                                                                        .map((img) => img.id);
                                                                    if (appointmentImageIds.length === 0) return null;
                                                                    return (
                                                                        <TreatmentImageThumbnails
                                                                            imageIds={appointmentImageIds}
                                                                            customerId={customerId}
                                                                            onImageClick={setSelectedImageId}
                                                                        />
                                                                    );
                                                                })()}
                                                            </>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell className="align-top whitespace-normal">
                                                    {treatment.medications.length > 0 ? (
                                                        <div className="space-y-1" data-notes-height-source>
                                                            {treatment.medications.map((med) => {
                                                                const region = med.medicationRegion || med.region?.name || '';
                                                                const name = med.medicationName || med.medicament?.name || '';
                                                                const quantity = String(med.medicationQuantity);
                                                                const unit = med.medicationUnit || med.medicament?.unit?.name || '';
                                                                return (
                                                                    <div key={med.id} className="bg-yellow-50 dark:bg-yellow-950/30 rounded px-1.5 py-0.5">
                                                                        <div className="text-sm break-words">
                                                                            {[region, name, `${quantity} ${unit}`.trim()].filter(Boolean).join(' · ')}
                                                                        </div>
                                                                        {med.notes && (
                                                                            <div className="text-xs italic text-muted-foreground break-words">
                                                                                {med.notes}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground block" data-notes-height-source>—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="align-top" onClick={(e) => e.stopPropagation()}>
                                                    {treatment.administeredByPractitionerId === currentPractitionerId ? (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => setEditTreatmentId(treatment.id)}>
                                                                    Bearbeiten
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    className="text-destructive"
                                                                    onClick={() => setDeleteTreatmentId(treatment.id)}
                                                                >
                                                                    Löschen
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : (
                                                        <Button variant="ghost" className="h-8 w-8 p-0 opacity-30 pointer-events-none">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Images Section */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            Bilder ({filteredImages.length}{filterAppointmentId ? ` / ${record.images.length}` : ''})
                        </CardTitle>
                        {imageAppointmentOptions.length > 0 && (
                            <Select
                                value={filterAppointmentId || '__all__'}
                                onValueChange={(value) => setFilterAppointmentId(value === '__all__' ? null : value)}
                            >
                                <SelectTrigger className="w-auto max-w-[300px] h-8 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">Alle Termine</SelectItem>
                                    {imageAppointmentOptions.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredImages.length === 0 ? (
                        <p className="text-gray-500 text-sm">
                            {record.images.length === 0 ? 'Keine Bilder vorhanden' : 'Keine Bilder für diesen Termin'}
                        </p>
                    ) : (
                        <ImageGallery
                            images={filteredImages}
                            customerId={customerId}
                            currentPractitionerId={currentPractitionerId}
                            selectedImage={selectedImageId}
                            onSelectImage={setSelectedImageId}
                            compareMode={compareMode}
                            onSetCompareMode={setCompareMode}
                            compareImageId={compareImageId}
                            onSetCompareImageId={setCompareImageId}
                        />
                    )}
                    {canUploadPhotos && (
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => setAddImageOpen(true)}>
                            <Camera className="h-4 w-4 mr-2" />
                            Bild hinzufügen
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* Dialogs */}
            <AddNoteDialog customerId={customerId} open={addNoteOpen} onOpenChange={setAddNoteOpen} />
            <AddImageDialog customerId={customerId} open={addImageOpen} onOpenChange={setAddImageOpen} />
            <AddImageDialog
                customerId={customerId}
                open={!!addImageForAppointmentId}
                onOpenChange={(open) => {
                    if (!open) setAddImageForAppointmentId(null);
                }}
                preselectedAppointmentId={addImageForAppointmentId}
            />
            <AddTreatmentDialog
                customerId={customerId}
                treatmentId={editTreatmentId}
                forceNew={!editTreatmentId}
                open={addTreatmentOpen || !!editTreatmentId}
                onOpenChange={(open) => {
                    if (!open) {
                        setAddTreatmentOpen(false);
                        setEditTreatmentId(null);
                    } else {
                        setAddTreatmentOpen(true);
                    }
                }}
                onSuccess={() => {
                    setEditTreatmentId(null);
                    utils.medicalRecord.getLatestTreatmentByAppointment.invalidate();
                }}
            />

            {/* Edit attribution only */}
            <AddTreatmentDialog
                customerId={customerId}
                treatmentId={editAttributionTreatmentId}
                forceAttributionStep
                open={!!editAttributionTreatmentId}
                onOpenChange={(open) => {
                    if (!open) setEditAttributionTreatmentId(null);
                }}
                onSuccess={() => {
                    setEditAttributionTreatmentId(null);
                    utils.medicalRecord.getLatestTreatmentByAppointment.invalidate();
                }}
            />

            {/* Documentation start from today's appointment */}
            <AddTreatmentDialog
                customerId={customerId}
                forceNew
                preselectedAppointmentId={docStartAppointmentId}
                open={!!docStartAppointmentId}
                onOpenChange={(open) => {
                    if (!open) {
                        setDocStartAppointmentId(null);
                    }
                }}
                onSuccess={() => {
                    setDocStartAppointmentId(null);
                    utils.medicalRecord.getLatestTreatmentByAppointment.invalidate();
                }}
            />

            {/* Delete Note Confirmation */}
            <AlertDialog open={!!deleteNoteId} onOpenChange={(open) => !open && setDeleteNoteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Notiz löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie diese Notiz wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-white hover:bg-destructive/90"
                            onClick={() => {
                                if (deleteNoteId) {
                                    deleteNoteMutation.mutate({ customerId, noteId: deleteNoteId });
                                }
                            }}
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Treatment Confirmation */}
            <AlertDialog open={!!deleteTreatmentId} onOpenChange={(open) => !open && setDeleteTreatmentId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Behandlung löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie diese Behandlung wirklich löschen? Alle zugehörigen Medikationen und Bilder werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-white hover:bg-destructive/90"
                            onClick={() => {
                                if (deleteTreatmentId) {
                                    deleteTreatmentMutation.mutate({ customerId, treatmentId: deleteTreatmentId });
                                }
                            }}
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const TREATMENT_NOTES_PROSE_CLASSES = [
    'prose prose-sm max-w-none dark:prose-invert break-words',
    '[&_p]:m-0 [&_p]:mb-2',
    '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2',
    '[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2',
    '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
    '[&_blockquote]:border-l-4 [&_blockquote]:border-muted-foreground [&_blockquote]:pl-4 [&_blockquote]:italic',
    '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2',
    '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2',
    '[&_li]:my-1',
    '[&_table]:border-collapse [&_table]:w-full [&_table]:my-2 [&_table]:table-fixed',
    '[&_td]:border [&_td]:border-border [&_td]:p-2',
    '[&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:bg-muted [&_th]:font-semibold',
    '[&_a]:text-primary [&_a]:underline',
].join(' ');

/** Height of the toggle button (h-4) plus its mt-0.5 */
const NOTES_TOGGLE_HEIGHT = 18;
/** Below this, a clipped preview is just noise, so only the toggle is shown */
const MIN_NOTES_PREVIEW_HEIGHT = 36;

/**
 * Collapsed, the notes only fill the height the row already has because of the
 * medication cell (marked with `data-notes-height-source`), so they never make the row taller.
 */
const TreatmentNotes = ({ html, expanded, onToggle }: { html: string; expanded: boolean; onToggle: () => void }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [layout, setLayout] = useState({ fits: false, previewHeight: 0 });

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        const content = contentRef.current;
        const cell = wrapper?.closest('td');
        const source = cell?.closest('tr')?.querySelector<HTMLElement>('[data-notes-height-source]');
        if (!wrapper || !content || !cell || !source) return;

        const measure = () => {
            const children = Array.from(cell.children);
            if (children.length === 0) return;
            const cellContentHeight = children[children.length - 1].getBoundingClientRect().bottom - children[0].getBoundingClientRect().top;
            const otherContentHeight = cellContentHeight - wrapper.getBoundingClientRect().height;
            const available = source.getBoundingClientRect().height - otherContentHeight;
            const fullHeight = content.scrollHeight;
            const fits = fullHeight <= available;
            const preview = fits ? fullHeight : available - NOTES_TOGGLE_HEIGHT;
            const previewHeight = preview >= MIN_NOTES_PREVIEW_HEIGHT ? Math.floor(preview) : 0;
            setLayout((prev) => (prev.fits === fits && prev.previewHeight === previewHeight ? prev : { fits, previewHeight }));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(cell);
        observer.observe(source);
        observer.observe(content);
        return () => observer.disconnect();
    }, [html]);

    const clipped = !expanded && !layout.fits;

    return (
        <div ref={wrapperRef}>
            <div
                className={cn(clipped && 'overflow-hidden')}
                style={clipped ? {
                    maxHeight: layout.previewHeight,
                    maskImage: 'linear-gradient(to bottom, black calc(100% - 1.5rem), transparent)',
                } : undefined}
            >
                <div ref={contentRef} className={TREATMENT_NOTES_PROSE_CLASSES} dangerouslySetInnerHTML={{ __html: html }} />
            </div>
            {!layout.fits && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 mt-0.5 gap-0.5 text-[11px] px-1 has-[>svg]:px-1 -ml-1 text-muted-foreground"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                >
                    {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                    {expanded ? 'Einklappen' : 'Ausklappen'}
                </Button>
            )}
        </div>
    );
};

// Small inline thumbnails for images linked to a treatment's appointment
const TreatmentImageThumbnails = ({
    imageIds,
    customerId,
    onImageClick,
}: {
    imageIds: string[];
    customerId: number;
    onImageClick: (imageId: string) => void;
}) => {
    const { data: urls } = trpc.medicalRecord.getBatchImageViewUrls.useQuery(
        { customerId, imageIds },
        { enabled: imageIds.length > 0, staleTime: 4 * 60 * 1000 }
    );

    if (imageIds.length === 0) return null;

    return (
        <div className="flex items-center gap-1 mt-2 flex-wrap">
            {imageIds.map((id) => {
                const url = urls?.[id];
                return (
                    <button
                        key={id}
                        type="button"
                        className="shrink-0 w-14 h-14 rounded overflow-hidden bg-gray-100 hover:ring-2 hover:ring-primary transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            onImageClick(id);
                        }}
                    >
                        {url ? (
                            <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

// Image gallery component with batch URL loading and auto-refresh
const IMAGE_TYPE_LABELS: Record<string, string> = {
    BEFORE: 'Vorher-Bild',
    AFTER: 'Nachher-Bild',
    OTHER: 'Sonstiges',
    COMPLICATION: 'Komplikation',
};

type ImageGalleryProps = {
    images: Array<{
        id: string;
        bodyRegion: string;
        bodyRegionId: string | null;
        description: string;
        imageType: string;
        createdAt: string;
        appointmentId: string | null;
        uploadedByPractitionerId: number | null;
        uploadedByPractitioner: {
            firstName: string;
            lastName: string;
        } | null;
    }>;
    customerId: number;
    currentPractitionerId: number | null;
    selectedImage: string | null;
    onSelectImage: (imageId: string | null) => void;
    compareMode: boolean;
    onSetCompareMode: (mode: boolean) => void;
    compareImageId: string | null;
    onSetCompareImageId: (imageId: string | null) => void;
};

const IMAGE_URL_REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes (URLs valid for 5 min)

type RotatableImageProps = {
    src: string;
    alt: string;
    rotation: number;
    /** CSS length of the frame height */
    height: string;
    /** CSS length of the frame width; needed because a percentage max-height can't reference the width */
    width: string;
    className?: string;
};

const RotatableImage = ({ src, alt, rotation, height, width, className }: RotatableImageProps) => {
    const sideways = rotation % 180 !== 0;
    return (
        <div className="relative w-full overflow-hidden" style={{ height }}>
            <img
                src={src}
                alt={alt}
                className={cn('absolute left-1/2 top-1/2 h-auto w-auto object-contain transition-transform duration-200', className)}
                style={{
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                    maxWidth: sideways ? height : width,
                    maxHeight: sideways ? width : height,
                }}
            />
        </div>
    );
};

const RotateButton = ({ onClick, className }: { onClick: () => void; className?: string }) => (
    <Button
        variant="ghost"
        size="icon"
        title="Drehen"
        className={cn('bg-black/50 hover:bg-black/70 text-white', className)}
        onClick={onClick}
    >
        <RotateCw className="h-5 w-5" />
    </Button>
);

const rotateBy90 = (r: number) => (r + 90) % 360;

/** Half of the compare dialog's inner width (dialog is capped at sm:max-w-7xl, minus p-4 and gap-1) */
const COMPARE_COLUMN_WIDTH = 'calc((min(80rem, 100vw - 2rem) - 2.25rem) / 2)';

const ImageGallery = ({ images, customerId, currentPractitionerId, selectedImage, onSelectImage, compareMode, onSetCompareMode, compareImageId, onSetCompareImageId }: ImageGalleryProps) => {
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
    const [editImageId, setEditImageId] = useState<string | null>(null);
    const [editBodyRegionId, setEditBodyRegionId] = useState<string>('');
    const [editDescription, setEditDescription] = useState('');
    const [editImageType, setEditImageType] = useState<string>('OTHER');
    const [editRegionSearch, setEditRegionSearch] = useState('');
    const [lightboxRotation, setLightboxRotation] = useState(0);
    const [compareLeftRotation, setCompareLeftRotation] = useState(0);
    const [compareRightRotation, setCompareRightRotation] = useState(0);

    useEffect(() => {
        setLightboxRotation(0);
        setCompareLeftRotation(0);
    }, [selectedImage]);

    useEffect(() => {
        setCompareRightRotation(0);
    }, [compareImageId]);

    useEffect(() => {
        setCompareLeftRotation(compareMode ? lightboxRotation : 0);
        setCompareRightRotation(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only carry over the rotation when entering compare mode
    }, [compareMode]);

    const utils = trpc.useUtils();

    const deleteImageMutation = trpc.medicalRecord.deleteImage.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            toast.success('Bild gelöscht');
            setDeleteImageId(null);
        },
        onError: (error) => toast.error('Fehler beim Löschen', { description: error.message }),
    });

    const updateImageMutation = trpc.medicalRecord.updateImage.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            toast.success('Bild aktualisiert');
        },
        onError: (error) => toast.error('Fehler beim Speichern', { description: error.message }),
    });

    const { data: treatmentRegions } = trpc.medicalRecord.getTreatmentRegions.useQuery();

    const regionItems = useMemo(() =>
        (treatmentRegions ?? [])
            .filter((r) => !editRegionSearch || r.name.toLowerCase().includes(editRegionSearch.toLowerCase()))
            .map((r) => ({ value: r.id, label: r.name })),
        [treatmentRegions, editRegionSearch]
    );

    const imageIds = images.map((img) => img.id);

    const batchQuery = trpc.medicalRecord.getBatchImageViewUrls.useQuery(
        { customerId, imageIds },
        { enabled: imageIds.length > 0, staleTime: IMAGE_URL_REFRESH_INTERVAL }
    );

    // Update URLs when query data changes
    useEffect(() => {
        if (batchQuery.data) {
            setImageUrls(batchQuery.data);
            setIsLoading(false);
        }
    }, [batchQuery.data]);

    // Auto-refresh URLs every 4 minutes
    useEffect(() => {
        if (imageIds.length === 0) {
            return;
        }

        const interval = setInterval(() => {
            batchQuery.refetch();
        }, IMAGE_URL_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [imageIds.length, batchQuery]);

    const handleImageClick = useCallback((imageId: string) => {
        onSelectImage(imageId);
    }, [onSelectImage]);

    const handleEditClick = useCallback((imageId: string) => {
        const img = images.find((i) => i.id === imageId);
        if (img) {
            setEditBodyRegionId(img.bodyRegionId || '');
            setEditDescription(img.description || '');
            setEditImageType(img.imageType || 'OTHER');
        }
        setEditRegionSearch('');
        setEditImageId(imageId);
    }, [images]);

    const handleCloseLightbox = useCallback(() => {
        onSelectImage(null);
    }, [onSelectImage]);

    const selectedImageData = selectedImage ? images.find((img) => img.id === selectedImage) : null;
    const selectedImageUrl = selectedImage ? imageUrls[selectedImage] : null;

    if (isLoading && imageIds.length > 0) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => (
                    <div key={image.id} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => {
                    const url = imageUrls[image.id];
                    return (
                        <div
                            key={image.id}
                            className="group relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                            onClick={() => handleImageClick(image.id)}
                        >
                            <div className="relative aspect-square">
                                {url ? (
                                    <img
                                        src={url}
                                        alt={image.description || image.bodyRegion || 'Medizinisches Bild'}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                    </div>
                                )}
                            </div>
                            <div className="p-2 text-xs space-y-0.5 flex">
                                <div className="flex-1 min-w-0">
                                    <p className="text-muted-foreground">
                                        {format(new Date(image.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                                    </p>
                                    <p className="text-muted-foreground">{IMAGE_TYPE_LABELS[image.imageType] ?? image.imageType}</p>
                                    {image.uploadedByPractitioner && (
                                        <p className="text-muted-foreground truncate">
                                            {image.uploadedByPractitioner.firstName} {image.uploadedByPractitioner.lastName}
                                        </p>
                                    )}
                                    {image.bodyRegion && <p className="font-medium truncate">{image.bodyRegion}</p>}
                                    {image.description && <p className="text-muted-foreground truncate">{image.description}</p>}
                                </div>
                                {image.uploadedByPractitionerId === currentPractitionerId && (
                                    <div className="flex items-start gap-1 ml-1 shrink-0">
                                        <button
                                            className="p-1 rounded text-muted-foreground hover:text-blue-600 hover:bg-blue-50"
                                            onClick={(e) => { e.stopPropagation(); handleEditClick(image.id); }}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50"
                                            onClick={(e) => { e.stopPropagation(); setDeleteImageId(image.id); }}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Lightbox Dialog - image only */}
            <Dialog open={!!selectedImage && !compareMode} onOpenChange={(open) => !open && handleCloseLightbox()}>
                <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
                    <div className="relative">
                        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
                            <RotateButton onClick={() => setLightboxRotation(rotateBy90)} />
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 bg-black/50 hover:bg-black/70 text-white text-xs px-2.5"
                                onClick={() => onSetCompareMode(true)}
                            >
                                <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                                Vergleichen
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="bg-black/50 hover:bg-black/70 text-white"
                                onClick={handleCloseLightbox}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        {selectedImageUrl ? (
                            <RotatableImage
                                src={selectedImageUrl}
                                alt={selectedImageData?.description || selectedImageData?.bodyRegion || 'Medizinisches Bild'}
                                rotation={lightboxRotation}
                                height="85vh"
                                width="min(80rem, 100vw - 2rem)"
                            />
                        ) : (
                            <div className="w-full h-96 flex items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Compare Picker Dialog */}
            <Dialog
                open={compareMode && !compareImageId}
                onOpenChange={(open) => {
                    if (!open) {
                        onSetCompareMode(false);
                    }
                }}
            >
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">Bild zum Vergleichen auswählen</h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {images.map((image) => {
                                const url = imageUrls[image.id];
                                const isOriginal = image.id === selectedImage;
                                return (
                                    <div
                                        key={image.id}
                                        className={cn(
                                            'relative rounded-lg overflow-hidden cursor-pointer transition-all border-2',
                                            isOriginal
                                                ? 'border-primary ring-2 ring-primary/30 opacity-60 cursor-default'
                                                : 'border-transparent hover:border-primary/50 hover:ring-2 hover:ring-primary/20',
                                        )}
                                        onClick={() => {
                                            if (!isOriginal) onSetCompareImageId(image.id);
                                        }}
                                    >
                                        <div className="aspect-square bg-gray-100">
                                            {url ? (
                                                <img
                                                    src={url}
                                                    alt={image.description || image.bodyRegion || 'Medizinisches Bild'}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-1.5 text-xs space-y-0.5">
                                            <p className="text-muted-foreground">
                                                {format(new Date(image.createdAt), 'dd.MM.yyyy', { locale: de })}
                                            </p>
                                            <p className="text-muted-foreground">{IMAGE_TYPE_LABELS[image.imageType] ?? image.imageType}</p>
                                            {image.bodyRegion && <p className="font-medium truncate">{image.bodyRegion}</p>}
                                        </div>
                                        {isOriginal && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                                <Badge variant="secondary" className="text-xs">Aktuelles Bild</Badge>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Side-by-Side Comparison Dialog */}
            <Dialog
                open={compareMode && !!compareImageId}
                onOpenChange={(open) => {
                    if (!open) {
                        onSetCompareMode(false);
                        onSetCompareImageId(null);
                    }
                }}
            >
                <DialogContent className="max-w-[95vw] w-full max-h-[95vh] p-0 overflow-hidden">
                    <div className="relative flex flex-col h-full">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
                            onClick={() => {
                                onSetCompareMode(false);
                                onSetCompareImageId(null);
                            }}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                        <div className="grid grid-cols-2 gap-1 p-4 pt-6 max-h-[90vh] overflow-y-auto">
                            {/* Original Image */}
                            <div className="relative flex flex-col items-center">
                                {selectedImage && imageUrls[selectedImage] ? (
                                    <>
                                        <RotateButton
                                            className="absolute top-2 left-2 z-10"
                                            onClick={() => setCompareLeftRotation(rotateBy90)}
                                        />
                                        <RotatableImage
                                            src={imageUrls[selectedImage]}
                                            alt={selectedImageData?.description || selectedImageData?.bodyRegion || 'Original'}
                                            rotation={compareLeftRotation}
                                            height="75vh"
                                            width={COMPARE_COLUMN_WIDTH}
                                            className="rounded"
                                        />
                                    </>
                                ) : (
                                    <div className="h-96 w-full flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                )}
                                {selectedImageData && (
                                    <div className="mt-2 text-center text-sm text-muted-foreground">
                                        <p>{format(new Date(selectedImageData.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
                                        <p>{IMAGE_TYPE_LABELS[selectedImageData.imageType] ?? selectedImageData.imageType}</p>
                                        {selectedImageData.bodyRegion && <p className="font-medium">{selectedImageData.bodyRegion}</p>}
                                    </div>
                                )}
                            </div>
                            {/* Comparison Image */}
                            {(() => {
                                const compareData = compareImageId ? images.find((img) => img.id === compareImageId) : null;
                                const compareUrl = compareImageId ? imageUrls[compareImageId] : null;
                                return (
                                    <div className="relative flex flex-col items-center">
                                        {compareUrl ? (
                                            <>
                                                <RotateButton
                                                    className="absolute top-2 left-2 z-10"
                                                    onClick={() => setCompareRightRotation(rotateBy90)}
                                                />
                                                <RotatableImage
                                                    src={compareUrl}
                                                    alt={compareData?.description || compareData?.bodyRegion || 'Vergleich'}
                                                    rotation={compareRightRotation}
                                                    height="75vh"
                                                    width={COMPARE_COLUMN_WIDTH}
                                                    className="rounded"
                                                />
                                            </>
                                        ) : (
                                            <div className="h-96 w-full flex items-center justify-center">
                                                <Loader2 className="h-8 w-8 animate-spin" />
                                            </div>
                                        )}
                                        {compareData && (
                                            <div className="mt-2 text-center text-sm text-muted-foreground">
                                                <p>{format(new Date(compareData.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
                                                <p>{IMAGE_TYPE_LABELS[compareData.imageType] ?? compareData.imageType}</p>
                                                {compareData.bodyRegion && <p className="font-medium">{compareData.bodyRegion}</p>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Image Dialog - small popup */}
            <Dialog open={!!editImageId} onOpenChange={(open) => !open && setEditImageId(null)}>
                <DialogContent className="sm:max-w-sm">
                    <div className="space-y-4">
                        <h3 className="font-medium text-sm">Bild bearbeiten</h3>
                        <div>
                            <Label className="text-xs">Bildtyp</Label>
                            <Select value={editImageType} onValueChange={setEditImageType}>
                                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BEFORE">Vorher-Bild</SelectItem>
                                    <SelectItem value="AFTER">Nachher-Bild</SelectItem>
                                    <SelectItem value="OTHER">Sonstiges</SelectItem>
                                    <SelectItem value="COMPLICATION">Komplikation</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Körperregion</Label>
                            <div className="mt-1">
                                <AutoComplete
                                    selectedValue={editBodyRegionId}
                                    onSelectedValueChange={setEditBodyRegionId}
                                    searchValue={editRegionSearch}
                                    onSearchValueChange={setEditRegionSearch}
                                    items={regionItems}
                                    placeholder="Region suchen..."
                                    emptyMessage="Keine Region gefunden"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Beschreibung</Label>
                            <Input
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Optionale Beschreibung"
                                className="mt-1 h-8 text-sm"
                            />
                        </div>
                        <div className="flex justify-end">
                            <Button
                                size="sm"
                                onClick={() => {
                                    if (editImageId) {
                                        updateImageMutation.mutate({
                                            customerId,
                                            imageId: editImageId,
                                            bodyRegionId: editBodyRegionId || null,
                                            description: editDescription,
                                            imageType: editImageType as 'BEFORE' | 'AFTER' | 'OTHER' | 'COMPLICATION',
                                        });
                                        setEditImageId(null);
                                    }
                                }}
                                disabled={updateImageMutation.isPending}
                            >
                                {updateImageMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : null}
                                Speichern
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Image Confirmation */}
            <AlertDialog open={!!deleteImageId} onOpenChange={(open) => !open && setDeleteImageId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bild löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie dieses Bild wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-white hover:bg-destructive/90"
                            onClick={() => {
                                if (deleteImageId) {
                                    deleteImageMutation.mutate({ customerId, imageId: deleteImageId });
                                }
                            }}
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default MedicalRecordSection;
